//import 'reflect-metadata';
import Loading from "@/components/loading/loading.vue";
import PageTitle from "@/components/page-title/page-title.vue";
import { GridLocale } from "@/locales/localeid";
import { mergeClasses } from "@/util/func";
import { wijmoGridResponsive } from "@/util/wijmo";
import { Button, Empty, Icon, Input, Pagination } from "ant-design-vue";
import classNames from "classnames";
import { Action, ActionItem, fmtMsg, MessageHelper, NotificationType } from "commonui";
import { cloneDeep, debounce, get, isArray, isUndefined, merge } from "lodash";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import ListStatesRadios from "@/components/list-states-radios/list-states-radios.vue";
import { CollectionView, EventArgs, SortDescription } from "wijmo/wijmo";
import {
	AllowMerging,
	AllowResizing,
	CellRangeEventArgs,
	CellType,
	Column,
	FlexGrid as IFlexGrid,
	FormatItemEventArgs,
	HeadersVisibility,
	SelectionMode
} from "wijmo/wijmo.grid";
import * as gridPdf from "wijmo/wijmo.grid.pdf";
import { FlexGridSearch as IFlexGridSearch } from "wijmo/wijmo.grid.search";
import { FlexGridXlsxConverter, XlsxFormatItemEventArgs } from "wijmo/wijmo.grid.xlsx";
import "wijmo/wijmo.touch";
import "wijmo/wijmo.vue2.grid";
import "wijmo/wijmo.vue2.grid.grouppanel";
import "wijmo/wijmo.vue2.grid.search";
import {
	HAlign,
	IWorkbookCell,
	Workbook,
	WorkbookCell,
	WorkbookColumn,
	WorkbookFont,
	WorkbookRow,
	WorkbookStyle,
	WorkSheet
} from "wijmo/wijmo.xlsx";
import { ListStates } from "@/util/enum";
import { ListStatesRadiosProps } from "@/components/list-states-radios/list-states-radios";
import { FlexGridContextMenu } from "./context-menu";

export interface IWijmoSort {
	column: string;
	ascending: boolean;
}
export interface GridRef {
	/** Whether to reload data from service */
	reload: () => void;
	collapseRows: () => void;
	setCurrentPage: (n: number) => void;
	setParamWatcher: (v: boolean) => void;
}

export interface ExportExcelOptions {
	buttonLocaleId?: string;
	buttonIcon?: string;
	filename?: string;
	includeColumns: {
		binding: string;
		header: string;
		width?: number;
	}[];
	excludeColumns: string[];
	formatData: (data: any) => any[];
	successMessage?: string;
	errorMessage?: string;
	noDataMessage?: string;
	onSave?: (base64: string | undefined) => void;
	onError?: (reason: any) => void;
	exportGroupFormatItem: (args: XlsxFormatItemEventArgs) => void;
	formatCell: (args: IWorkbookCell, binding: string, record: any) => void;
}

interface VisibleColumnOptions {
	isVisble: boolean;
	index: number;
}
export interface ExportPdfOptions {
	buttonLocaleId?: string;
	buttonIcon?: string;
	filename?: string;
	scaleMode: gridPdf.ScaleMode;
	customCellContent: boolean;
	formatItem: (args: any) => void;
	//orientation: gridPdf.PdfPageOrientation;
	exportMode: gridPdf.ExportMode;
	excludeColumns: string[];
}

const defaultExportOptions = {
	buttonLocaleId: fmtMsg(GridLocale.GirdExportExcelButton),
	buttonIcon: "file-excel",
	includeColumns: [],
	excludeColumns: [],
	filename: "Workbook",
	formatData: (data: any) => data,
	formatCell: (cell: IWorkbookCell, binding: string, record: any) => {
		if (isArray(cell.value)) {
			cell.value = cell.value.join(",");
		}
	},
	exportGroupFormatItem: (args: XlsxFormatItemEventArgs) => {
		const cell: IWorkbookCell = args.xlsxCell;
		if (args.panel.cellType === CellType.ColumnHeader) {
			cell.style!.font = { bold: true };
		}
	}
};

const defaultPdfExportOptions = {
	buttonLocaleId: fmtMsg(GridLocale.GirdExportPdfButton),
	buttonIcon: "file-pdf",
	scaleMode: gridPdf.ScaleMode.PageWidth,
	customCellContent: true,
	filename: "GridPdf",
	formatItem: (args: any) => {},
	//orientation: gridPdf.PdfPageOrientation.Portrait,
	exportMode: gridPdf.ExportMode.All,
	excludeColumns: []
};

// format item code example
// if (args.panel.cellType === grid.CellType.Cell && args.panel.columns[args.col].binding === 'color') {
// 	args.style.backgroundColor = args.data;
// }

const defaultSearchOptions = {
	label: "Search",
	enterButton: false,
	searchOnClick: false,
	showBelowTitleAndActions: false,
	takeSearchTermFromProps: false
};

const defaultListStateOptions = {
	value: ListStates.Active,
	allCnt: 0,
	activeCnt: 0,
	inActiveCnt: 0
};

const defaultPageSize = 10;
@Component({
	components: {
		Pagination,
		AntdInput: Input,
		Loading,
		Empty,
		Input,
		Button,
		Icon,
		InputSearch: Input.Search,
		Action,
		ActionItem,
		PageTitle,
		ListStatesRadios
	}
})
export default class Grid extends Vue {
	@Prop() allowAddNew?: boolean;
	@Prop() allowMerging?: AllowMerging;
	@Prop({ default: AllowResizing.Columns }) allowResizing?: AllowResizing;
	@Prop({ default: true }) allowSorting?: boolean;
	@Prop({ default: false }) showAsTree?: boolean;
	@Prop({ default: () => ["children"] }) childItemPath?: string[];
	@Prop({ default: HeadersVisibility.Column }) headersVisibility?: HeadersVisibility;
	@Prop({ default: SelectionMode.ListBox }) selectionMode?: SelectionMode;
	@Prop() showMarquee?: boolean;
	@Prop() showSelectedHeaders?: HeadersVisibility;
	@Prop({
		default: () =>
			new Promise((resolve, reject) => {
				resolve();
			})
	})
	setTreeItemSourceForExport?: (s: IFlexGrid) => Promise<any>;
	@Prop({ default: true }) stickyHeaders?: boolean;
	@Prop() isReadOnly?: boolean;
	@Prop({ default: 0 }) alternatingRowStep?: number;
	@Prop() onGroupCollapsedChanged?: (s: IFlexGrid, e: any) => any;
	@Prop() formatItem?: (s: IFlexGrid, e: FormatItemEventArgs) => any;
	@Prop() initialized?: (grid: IFlexGrid) => any;
	@Prop() onSortedColumn?: (e: CellRangeEventArgs) => void;
	@Prop() defaultSort?: IWijmoSort;
	/** Whether to enable the list states */
	@Prop() listStateShow?: boolean;
	/** Whether to pass props to list states component */
	@Prop() listStateOptions?: ListStatesRadiosProps;
	@Prop() loadedRows?: (s: IFlexGrid, e: EventArgs) => void;
	/** Whether to set local dataSource */
	@Prop() itemsSource?: CollectionView | any[];
	/** Whether to show a loading indicator over grid */
	@Prop() loading?: boolean;
	/** Whether service will be immediately triggered or not on mount */
	@Prop({ default: true }) immediateServiceTrigger?: boolean;
	/** Whether to turn the grid into a virtual grid. */
	@Prop() serviceFunction?: (params: object) => Promise<any>;
	/** Whether to set master query params to send to service with each request */
	@Prop() serviceParams?: () => object;
	/** Whether to format data before setting it to grid. */
	@Prop({ default: (data: any) => data }) serviceFormatData?: (data: any) => any[];
	/** Whether to enable search on the grid. Currently local search is not supported, it only works when serviceFunction is passed. */
	@Prop() search?: boolean;
	@Prop({
		default: () => defaultSearchOptions
	})
	searchOptions?: {
		/** Whether to set a label to search. */
		label?: string;
		/** Whether to set a callback on search. */
		onSearch?: (filter: string) => void;
		/** Whether to show button at the end */
		enterButton?: boolean | string;
		/** Whether to search on click the search button (false means onchange search) */
		searchOnClick?: boolean;
		/** Should show the search input control under the 'title' slot and actions*/
		showBelowTitleAndActions?: boolean;
		/** Take search term from props */
		takeSearchTermFromProps?: boolean;
		/** Search Term */
		searchTerm?: null | string;
		/** Value to show in input field */
		searchInputValue?: null | string;
		/** Seach Value Changed function */
		searchValueChanged?: (v: string) => void;
		/** Reset the search term in parent component */
		resetSeachTerm?: () => void;
		/** Search label */
		searchLabel?: string;
	};
	/** Whether allow grid to exported to an excel. */
	@Prop({ default: false }) allowExport?: boolean;
	/** Whether allow grid to exported to an pdf. */
	@Prop({ default: false }) allowPdfExport?: boolean;
	@Prop({ default: false }) autoRowHeights?: boolean;

	/** Pdf export options*/
	@Prop({ default: () => defaultPdfExportOptions })
	pdfExportOptions?: ExportPdfOptions;

	/** Excel export options*/
	@Prop({
		default: () => defaultExportOptions
	})
	exportOptions?: ExportExcelOptions;
	/** Whether to enable pagination. */
	@Prop({ default: false }) pagination?: boolean;
	/** Whether to set pagination size. Default Value: 10 */
	@Prop({ default: defaultPageSize }) paginationSize?: number;
	/** Whether to show a title of the grid */
	@Prop() title?: string;
	/** Whether to add action/link/button to the grid. They will be reandered at the right of the grid above header. */
	@Prop({ default: () => [] }) actions?: any;
	/** Whether to enable the grouping */
	@Prop() grouping?: boolean;
	/** Whether to set group bar text. */
	@Prop() groupBarText?: string;
	/** A callback of refresh the group panel. */
	@Prop() onRefreshed?: (grid: any) => any;
	/** Whether to add a css class to grid wrapper. */
	@Prop() className?: string;
	/** Whether to freeze the columns*/
	@Prop({ default: 0 }) frozenColumns?: number;
	/** If default row height should be updated (make it false if want to override) */
	@Prop({ default: 40 }) rowHeight?: number;
	//** Callback to pass currentPage to parent component */
	@Prop({ default: (currentPage: number) => {} }) onPaginationChange?: (currentPage: number) => void;
	//** Callback to pass currentSort to parent component */
	@Prop({ default: (currentSort: IWijmoSort) => {} }) onSortChange?: (currentSort: IWijmoSort) => void;
	//** Default currentPage from parent component */
	@Prop({ default: 1 }) defaultCurrentPage?: number;
	//** Default sort from parent component */
	@Prop({ default: () => ({ column: "", ascending: false }) })
	/** Whether to show search input in header for grid. */
	@Prop({ default: true })
	isUnitBased?: boolean;
	defaultSortFromParent?: IWijmoSort;
	@Prop({ default: 1 }) total?: number;
	@Prop({ default: false }) paginationOnServerSide = false;
	//** In case pagination is on server side, this function will be called to fetch all items */
	@Prop() fetchAllItemsSource?: () => Promise<any>;
	public exportMeta: ExportExcelOptions = defaultExportOptions;
	public searchMeta: any = defaultSearchOptions;
	public exportPdfMeta: ExportPdfOptions = defaultPdfExportOptions;
	public collectionView: CollectionView | null = null;
	public currentPage: number = this.defaultCurrentPage || 1;
	public totalCount: number | null = null;
	public currentSort: IWijmoSort | undefined = undefined;
	public filter: string = "";
	public loadingState: boolean = false;
	public gridRef!: IFlexGrid;
	public gridSearchRef!: IFlexGridSearch;
	public isSortChange: boolean = false;
	public hiddenGridRef!: IFlexGrid;
	public hiddenCollectionView: CollectionView | null = null;
	public isGroupPanelVisible: boolean = false;
	public overlayVisible: boolean = true;
	public listStateOptionsState: ListStatesRadiosProps = defaultListStateOptions;
	public runParamWatcher: boolean = true;

	beforeMount() {}

	created() {
		this.exportMeta = merge(cloneDeep(defaultExportOptions), this.exportOptions);
		this.searchMeta = merge(cloneDeep(defaultSearchOptions), this.searchOptions);
		this.listStateOptionsState = merge(cloneDeep(defaultListStateOptions), this.listStateOptions);
		this.exportPdfMeta = merge(cloneDeep(defaultPdfExportOptions), this.pdfExportOptions);
		this.currentSort = this.defaultSortFromParent?.column ? this.defaultSortFromParent : this.defaultSort;
		if (this.serviceFunction && this.immediateServiceTrigger) {
			this.getData();
		}
		this.setFlexGridOnSearchCallback();
	}

	mounted() {
		this.collapseRows();
	}

	collapseRows() {
		if (this.showAsTree) {
			this.gridRef?.collapseGroupsToLevel(0);
		}
	}

	get isLoading() {
		return this.loading || this.loadingState;
	}

	setLoadingState(value: boolean) {
		this.loadingState = value;
	}

	get noData() {
		return (!this.collectionView || !this.collectionView.sourceCollection.length) && !this.isLoading;
	}

	get noDataMessage() {
		return this.localizedString(this.gridLocales.GridNoRecordFoundMessage);
	}

	get renderHeader() {
		if (!this.search && !this.title && !this.actions.length && !this.allowExport) {
			return false;
		}

		return true;
	}

	get renderPagination() {
		return this.pagination && this.collectionView && !this.noData;
	}

	get mergedClass() {
		return classNames("wgrid", this.className);
	}

	onSearchInit(searchRef: IFlexGridSearch) {
		this.gridSearchRef = searchRef;
	}

	onGridSearch(value) {
		if (this.searchMeta.searchOnClick) {
			this.runParamWatcher = false;
		}
		this.setCurrentPage(1);
		if (this.searchMeta.onSearch) {
			this.searchMeta.onSearch(value);
		}
	}

	filterChanged(e: InputEvent & any) {
		if (this.searchMeta.searchOnClick) {
			this.runParamWatcher = false;
		}
		if (this.searchMeta.takeSearchTermFromProps && this.searchOptions && this.searchOptions.searchValueChanged) {
			this.searchOptions.searchValueChanged(e.target?.value);
		} else {
			this.filter = e.target?.value;
		}
	}

	setCurrentPage(pageNumber: number, force = false) {
		this.onPaginationChange?.(pageNumber);
		if (force) {
			this.runParamWatcher = true;
		}
		this.currentPage = pageNumber;
		this.$emit("pageChange", pageNumber);
	}

	public setParamWatcher(value: boolean) {
		this.runParamWatcher = value;
	}

	showGroupPanel() {
		const showPanel = () => {
			this.isGroupPanelVisible = true;
			if (this.serviceFunction) {
				this.setCurrentPage(1);
				this.currentSort = this.defaultSort;
				this.filter = "";
				this.getData();
			}
		};
		if (this.searchOptions && this.searchOptions.resetSeachTerm && this.searchOptions.takeSearchTermFromProps) {
			this.searchOptions.resetSeachTerm();
			Vue.nextTick(() => showPanel());
		} else {
			showPanel();
		}
	}

	get showGroupPanelLink() {
		return this.grouping && !this.isGroupPanelVisible;
	}

	@Watch("itemsSource", { deep: true, immediate: true })
	onItemSourceChange(val: any[], oldVal: any[]) {
		if (this.itemsSource instanceof CollectionView) {
			this.changeCollectionView(cloneDeep(this.itemsSource.items));
		} else {
			this.changeCollectionView(this.itemsSource!);
		}
	}

	@Watch("total", { deep: true })
	onTotalChange() {
		if (this.total) {
			this.totalCount = this.total;
		}
	}

	@Watch("listStateOptions", { deep: true })
	onListStatePropChange(val: any, oldVal: any) {
		if (!this.serviceFunction) {
			this.listStateOptionsState = merge(cloneDeep(this.listStateOptionsState), val);
		}
	}

	@Watch("filter")
	@Watch("currentPage")
	@Watch("isSortChange")
	@Watch("listStateOptionsState.value")
	onParamChange() {
		if (this.runParamWatcher) {
			if (this.serviceFunction && !this.isGroupPanelVisible) {
				this.getData();
			} else {
				this.collectionView?.moveToPage(this.currentPage - 1);
			}
		} else {
			this.runParamWatcher = true;
		}
	}

	reload() {
		if (this.serviceFunction) {
			this.getData();
		} else {
			MessageHelper.Message(
				NotificationType.Warning,
				this.localizedString(this.gridLocales.GridServiceFunctionError)
			);
		}
	}

	getData() {
		this.loadingState && this.debouncedGetData.cancel();
		this.debouncedGetData();
	}

	public debouncedGetData = debounce(this.getDataFunc, 250);

	getDataFunc() {
		this.setLoadingState(true);
		const params = this.getParams();
		if (this.isGroupPanelVisible) {
			delete params.limit;
		}
		this.serviceFunction!(params)
			.then((data) => {
				this.setDataToCollectionView(data);
				this.setLoadingState(false);
				this.overlayHandler();
			})
			.catch(() => this.setLoadingState(false));
	}

	getParams() {
		const serviceParams = !isUndefined(this.serviceParams) ? this.serviceParams() : null;
		const params: any = {
			...serviceParams,
			keyword: this.searchOptions?.takeSearchTermFromProps ? this.searchOptions?.searchTerm : this.filter
		};

		if (this.pagination) {
			params.limit = this.paginationSize;
			params.offset = (this.currentPage - 1) * this.getPageSize();
		}

		if (this.currentSort) {
			params.sortBy = this.currentSort.column;
			params.isDescending = !this.currentSort.ascending;
		}

		if (this.listStateShow) {
			switch (this.listStateOptionsState.value) {
				case ListStates.All:
					params.includePendingInvite = true;
					break;
				case ListStates.Active:
					params.disabled = false;
					break;
				case ListStates.Inactive:
					params.disabled = true;
					break;
				case ListStates.Pending:
					params.includePendingInvite = true;
					params.includeOnlyPending = true;
					break;
				case ListStates.Future:
					// need to work on it
					break;
			}
		}

		return params;
	}

	onListStateChange(value: ListStates) {
		this.setCurrentPage(1);
		this.listStateOptionsState = { ...this.listStateOptionsState, value };
		if (!isUndefined(this.listStateOptionsState.onChange)) {
			this.listStateOptionsState.onChange(value);
		}
	}

	get mergeListStateClass() {
		let className = "";
		if (this.listStateOptionsState && this.listStateOptionsState.className) {
			className = this.listStateOptionsState.className;
		}
		return mergeClasses("wgrid__state", className);
	}

	setDataToCollectionView(data: any) {
		// Save data to collection
		const records = this.serviceFormatData!(data);
		this.changeCollectionView(records);

		// Saving total count to state
		this.setTotalCountWrapper(data);

		// Saving list stated counts to state
		if (data.extraData) {
			const {
				activeCnt,
				allCnt,
				rwAssignedCount,
				vsAssignedCount,
				exposureCount,
				futureCnt: futureCount,
				inActiveCnt,
				pendingCount
			} = data.extraData;
			this.listStateOptionsState = {
				...this.listStateOptionsState,
				activeCnt,
				allCnt,
				rwAssignedCount,
				vsAssignedCount,
				exposureCount,
				futureCount,
				inActiveCnt,
				pendingCount
			};
		}
	}

	setTotalCountWrapper(data: any) {
		if (!this.listStateShow || !data.extraData) {
			return (this.totalCount = data.totalCount);
		}

		let count: number | null = null;
		switch (this.listStateOptionsState.value) {
			case ListStates.All:
				count = data.extraData.allCnt;
				break;
			case ListStates.Active:
				count = data.extraData.activeCnt;
				break;
			case ListStates.Inactive:
				count = data.extraData.inActiveCnt;
				break;
			case ListStates.Pending:
				count = data.extraData.pendingCount;
				break;
			case ListStates.Future:
				count = data.extraData.futureCnt;
				break;
			case ListStates.Exposure:
				count = data.extraData.exposureCount;
				break;
			case ListStates.RWAssigned:
				count = data.extraData.rwAssignedCount;
				break;
			case ListStates.VSAssigned:
				count = data.extraData.vsAssignedCount;
				break;
		}

		this.totalCount = count;
	}

	changeCollectionView(records: any[] | undefined) {
		const newCollectionView = new CollectionView(records);
		if ((!this.serviceFunction && this.pagination) || this.isGroupPanelVisible) {
			newCollectionView.pageSize = this.getPageSize();
			if (!this.paginationOnServerSide) {
				this.setCurrentPage(1);
			}
			//newCollectionView.moveToPage(this.currentPage - 1);
		}

		if (this.currentSort) {
			newCollectionView.sortDescriptions.push(
				new SortDescription(this.currentSort.column, this.currentSort.ascending)
			);
		}

		this.collectionView = newCollectionView;
	}

	getPageSize() {
		return isUndefined(this.paginationSize) ? defaultPageSize : this.paginationSize;
	}

	onHiddenGridInit(grid: IFlexGrid) {
		this.hiddenGridRef = grid;
		wijmoGridResponsive(grid);
	}

	onGridInit(grid: IFlexGrid): void {
		this.gridRef = grid;
		if (this.initialized) {
			this.initialized(grid);
		}

		grid.rows.defaultSize = this.rowHeight as number;
		grid.columnHeaders.rows.defaultSize = this.rowHeight as number;

		// Make the grid responsive with adding horizontal scrollbar
		wijmoGridResponsive(grid);

		// Calling overlay handler to handle grid fluctuation
		if (!this.serviceFunction || !this.immediateServiceTrigger) {
			this.overlayHandler();
		}

		// Listen sorting changes
		grid.sortedColumn.addHandler((flex, args) => {
			const sortDescription = grid.collectionView.sortDescriptions[0];
			this.currentSort = {
				column: sortDescription.property,
				ascending: sortDescription.ascending
			};
			this.onSortChange?.(this.currentSort);
			if (this.pagination) {
				this.isSortChange = !this.isSortChange;
			}
		});

		// Render context menu
		new FlexGridContextMenu(grid);
	}

	overlayHandler(): void {
		setTimeout(() => {
			this.overlayVisible = false;
		}, 1500);
	}

	localizedString(id: string, params?: {}) {
		return fmtMsg(id, params);
	}

	get gridLocales() {
		return GridLocale;
	}

	onGroupRefresh() {
		this.onRefreshed && this.onRefreshed(this.gridRef);
	}

	setFlexGridOnSearchCallback() {
		if (!this.searchMeta.onSearch || !this.gridSearchRef) {
			return null;
		}
		this.gridSearchRef.inputElement.oninput = () => {
			this.searchMeta.onSearch!(this.gridSearchRef.inputElement.value);
		};
	}

	exportGrid() {
		if (this.noData) {
			return;
		}
		if (this.paginationOnServerSide) {
			return this.exportAllItemsSource();
		} else if (this.serviceFunction && !this.isGroupPanelVisible) {
			return this.exportFromServiceData();
		} else if (
			this.isGroupPanelVisible &&
			this.gridRef?.collectionView.groups &&
			this.gridRef?.collectionView.groups.length > 0
		) {
			return this.designGroupExcelAndDownload();
		} else if (this.showAsTree) {
			this.designTreeExcelAndDownload();
			return;
		}
		return this.exportLocalGridData();
	}

	exportLocalGridData() {
		const view = this.gridRef?.collectionView as CollectionView;
		const _oldPageSize = view.pageSize,
			_oldPageIndex = view.pageIndex,
			_oldRowSize = this.gridRef?.rows.defaultSize;
		// set the properties as required
		this.gridRef?.beginUpdate();
		view.pageSize = 0;
		this.desingExcelAndDownload(this.gridRef?.collectionView.items, true);
		view.pageSize = _oldPageSize;
		view.moveToPage(_oldPageIndex);
		this.gridRef?.endUpdate();
	}

	exportFromServiceData() {
		const params = this.getParams();
		params.keyword = "";
		params.offset = 0;
		delete params.limit;
		this.setLoadingState(true);
		this.serviceFunction!(params)
			.then((data) => {
				this.desingExcelAndDownload(data, false);
				this.setLoadingState(false);
			})
			.catch(() => this.setLoadingState(false));
	}

	exportAllItemsSource() {
		if (!this.fetchAllItemsSource) return;
		this.setLoadingState(true);
		this.fetchAllItemsSource()
			.then((data) => {
				this.desingExcelAndDownload(data, false);
			})
			.catch((ex) => console.error(ex))
			.finally(() => this.setLoadingState(false));
	}

	async designTreeExcelAndDownload() {
		this.setLoadingState(true);

		if (!isUndefined(this.setTreeItemSourceForExport)) {
			this.setTreeItemSourceForExport(this.gridRef)
				.then((data) => {
					this.downloadTreeExcel();
				})
				.catch((ex) => {
					this.setLoadingState(false);
				});
		} else {
			this.downloadTreeExcel();
		}
	}

	downloadTreeExcel() {
		const { filename, exportGroupFormatItem, excludeColumns } = this.exportMeta;
		this.gridRef?.beginUpdate();
		const columnsOldState: VisibleColumnOptions[] = this.excludeCloumnsFromExporting(excludeColumns, this.gridRef);
		FlexGridXlsxConverter.saveAsync(
			this.gridRef!,
			{
				includeColumnHeaders: true,
				includeCellStyles: false,
				formatItem: exportGroupFormatItem
			},
			`${filename || "Workbook"}.xlsx`
		);
		this.resetColumnsVisibleState(columnsOldState, this.gridRef);
		this.gridRef?.endUpdate();
		this.setLoadingState(false);
	}

	designGroupExcelAndDownload() {
		const { filename, exportGroupFormatItem, excludeColumns } = this.exportMeta;
		const view = this.gridRef?.collectionView as CollectionView;
		const _oldPageSize = view.pageSize,
			_oldPageIndex = view.pageIndex,
			_oldRowSize = this.gridRef?.rows.defaultSize;
		// set the properties as required
		this.gridRef?.beginUpdate();
		view.pageSize = 0;
		const columnsOldState: VisibleColumnOptions[] = this.excludeCloumnsFromExporting(excludeColumns, this.gridRef);
		FlexGridXlsxConverter.saveAsync(
			this.gridRef!,
			{
				includeColumnHeaders: true,
				includeCellStyles: false,
				formatItem: exportGroupFormatItem
			},
			`${filename || "Workbook"}.xlsx`
		);
		this.resetColumnsVisibleState(columnsOldState, this.gridRef);
		view.pageSize = _oldPageSize;
		view.moveToPage(_oldPageIndex);
		this.gridRef?.endUpdate();
	}

	desingExcelAndDownload(data, skipFormatting) {
		const {
			filename,
			formatData,
			includeColumns,
			formatCell,
			excludeColumns,
			onSave,
			onError,
			successMessage,
			errorMessage,
			noDataMessage
		} = this.exportMeta;
		const records = skipFormatting ? data : formatData(data);
		// Skip of no records are available in data
		if (!records.length) {
			return MessageHelper.Message(NotificationType.Warning, noDataMessage!);
		}

		// Beginning of creation the workbook
		const workbook = new Workbook();
		const sheet = new WorkSheet();
		const columns = [
			...this.gridRef?.columns.filter(
				(column) =>
					!(excludeColumns.includes(column.binding) || excludeColumns.includes(column.name)) &&
					(column.binding || column.name)
			),
			...includeColumns
		] as Column[];
		// Adding header row
		const headerRow = new WorkbookRow();
		headerRow.style = new WorkbookStyle();
		headerRow.style.font = new WorkbookFont();
		headerRow.style.font.bold = true;
		headerRow.style.hAlign = HAlign.Center;

		// Adding columns and header cells
		columns.forEach((c, index) => {
			// Sheet Column
			const column = new WorkbookColumn();

			// Should be a valid float value like "54.5" or 54.
			if (!Number.isNaN(parseFloat(c.width))) {
				column.width = c.width;
			}
			sheet.columns.push(column);

			// Header cell
			const headerCell = new WorkbookCell();
			headerCell.value = c.header;
			headerRow.cells.push(headerCell);
		});
		sheet.rows.push(headerRow);

		// Adding records
		records.forEach((record) => {
			const row = new WorkbookRow();
			columns.forEach((c) => {
				const cell = new WorkbookCell();
				cell.value = get(record, c.binding);
				formatCell(cell, c.binding, record);
				row.cells.push(cell);
			});
			sheet.rows.push(row);
		});

		workbook.sheets.push(sheet);

		// Saving the workbook
		workbook.saveAsync(
			`${filename || "Workbook"}.xlsx`,
			(base64) => {
				if (onSave) {
					onSave(base64!);
				}
				if (successMessage) {
					MessageHelper.Message(NotificationType.Success, successMessage);
				}
			},
			(reason) => {
				if (onError) {
					onError(reason);
				}
				if (errorMessage) {
					MessageHelper.Message(NotificationType.Success, errorMessage);
				}
			}
		);
	}

	excludeCloumnsFromExporting(excludeColumns: string[], grid: IFlexGrid): VisibleColumnOptions[] {
		const columnsOldState: VisibleColumnOptions[] = [];
		excludeColumns.forEach((col) => {
			const colIndex = grid.columns.findIndex(
				(c) => (!isUndefined(c.binding) && c.binding === col) || (!isUndefined(c.name) && c.name === col)
			);
			if (colIndex > -1 && grid.columns[colIndex].visible) {
				columnsOldState.push({ index: colIndex, isVisble: grid.columns[colIndex].visible });
				grid.columns[colIndex].visible = false;
			}
		});

		return columnsOldState;
	}

	getColumnSize(columns): number {
		// No of columns in grid of NC+U based components
		if (columns.length === 5) {
			return 220;
		}
		// No of columns in grid of NC+NU based components
		else if (columns.length === 4) {
			return 250;
		}
		return columns.defaultSize;
	}

	downLoadPdf(grid: IFlexGrid, isVisibleGrid: boolean) {
		const view = grid.collectionView as CollectionView;
		const { filename, scaleMode, customCellContent, formatItem, exportMode, excludeColumns } = this.exportPdfMeta;
		const _oldPageSize = view.pageSize,
			_oldPageIndex = view.pageIndex,
			_oldRowSize = grid.rows.defaultSize,
			_oldColumnSize = grid.columns.defaultSize;

		if (isVisibleGrid && this.pagination) {
			// set the properties as required
			grid.beginUpdate();
			//grid.headersVisibility = wjcGrid.HeadersVisibility.Column;
			grid.rows.defaultSize = 20;
			view.pageSize = 0;
		}

		const columnsOldState: VisibleColumnOptions[] = this.excludeCloumnsFromExporting(excludeColumns, grid);

		grid.columns.defaultSize = this.getColumnSize(grid.columns);

		gridPdf.FlexGridPdfConverter.export(grid, `${filename || "GridExport"}.pdf`, {
			//   maxPages: 10,
			scaleMode: scaleMode,
			exportMode: exportMode,
			customCellContent: customCellContent,
			formatItem: formatItem,
			documentOptions: {
				compress: false,
				// pageSettings: {
				// 	layout: orientation
				// },
				header: { declarative: { text: "\t&[Page] of &[Pages]" } },
				footer: { declarative: { text: "\t&[Page] of &[Pages]" } },
				info: { author: "Grapecity", title: "ExportPdf" }
			},
			styles: {
				cellStyle: { backgroundColor: "#ffffff", borderColor: "#c6c6c6" },
				altCellStyle: { backgroundColor: "#f9f9f9" },
				groupCellStyle: { backgroundColor: "#dddddd" },
				headerCellStyle: { backgroundColor: "#eaeaea" }
			}
		});

		this.resetColumnsVisibleState(columnsOldState, grid);

		if (isVisibleGrid && this.pagination) {
			// set the properties to their original values
			//grid.headersVisibility = wjcGrid.HeadersVisibility.All;
			grid.rows.defaultSize = _oldRowSize;
			grid.columns.defaultSize = _oldColumnSize;
			view.pageSize = _oldPageSize;
			view.moveToPage(_oldPageIndex);
			grid.endUpdate();
		}
	}

	resetColumnsVisibleState(columnsOldState: VisibleColumnOptions[], grid: IFlexGrid) {
		columnsOldState.forEach((col) => {
			grid.columns[col.index].visible = col.isVisble;
		});
	}

	exportPdf() {
		if (this.noData) {
			return;
		}
		if (this.serviceFunction && !this.isGroupPanelVisible) {
			const params = this.getParams();
			params.keyword = "";
			params.offset = 0;
			delete params.limit;
			this.setLoadingState(true);
			this.serviceFunction(params)
				.then((data) => {
					const records = this.serviceFormatData!(data);
					const newCollectionView = new CollectionView(records);
					this.hiddenCollectionView = newCollectionView;
					setTimeout(() => {
						this.downLoadPdf(this.hiddenGridRef!, false);
						this.setLoadingState(false);
						const emptyCollectionView = new CollectionView([]);
						this.hiddenCollectionView = emptyCollectionView;
					}, 3000);
				})
				.catch(() => this.setLoadingState(false));
		} else {
			this.downLoadPdf(this.gridRef, true);
		}
	}
}
